import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Menu, ChevronDown, ChevronRight } from "lucide-react";

const MobileMenu = () => {
  const [open, setOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const closeMenu = () => setOpen(false);

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden">
          <Menu className="h-5 w-5" />
          <span className="sr-only">Open menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[85vw] max-w-sm overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-left">Menu</SheetTitle>
        </SheetHeader>
        <div className="flex flex-col gap-1 mt-6">
          <Link 
            to="/why-spharaka" 
            onClick={closeMenu}
            className="text-sm font-medium hover:text-[#8932c8] transition-colors py-3 px-2 rounded-md hover:bg-slate-50"
          >
            Why Spharaka?
          </Link>
          
          {/* Products Section */}
          <div className="border-b border-slate-100">
            <button 
              onClick={() => toggleSection('products')}
              className="w-full flex items-center justify-between text-sm font-medium py-3 px-2 rounded-md hover:bg-slate-50"
            >
              <span>Products</span>
              {expandedSection === 'products' ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
            {expandedSection === 'products' && (
              <div className="pl-4 pb-2 space-y-1 border-l-2 border-[#8932c8]/20 ml-2">
                <Link to="/products" onClick={closeMenu} className="block text-sm py-2 px-2 hover:text-[#8932c8] hover:bg-slate-50 rounded-md">
                  All Products
                </Link>
                <Link to="/products/spharakaxdr" onClick={closeMenu} className="block text-sm py-2 px-2 hover:text-[#8932c8] hover:bg-slate-50 rounded-md">
                  Spharaka XDR
                </Link>
                <Link to="/products/auraxp" onClick={closeMenu} className="block text-sm py-2 px-2 hover:text-[#8932c8] hover:bg-slate-50 rounded-md">
                  AuraXP
                </Link>
                <Link to="/products/ishwar" onClick={closeMenu} className="block text-sm py-2 px-2 hover:text-[#8932c8] hover:bg-slate-50 rounded-md">
                  ISHWAR
                </Link>
              </div>
            )}
          </div>
          
          {/* Solutions Section */}
          <div className="border-b border-slate-100">
            <button 
              onClick={() => toggleSection('solutions')}
              className="w-full flex items-center justify-between text-sm font-medium py-3 px-2 rounded-md hover:bg-slate-50"
            >
              <span>Solutions</span>
              {expandedSection === 'solutions' ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
            {expandedSection === 'solutions' && (
              <div className="pl-4 pb-2 space-y-1 border-l-2 border-[#8932c8]/20 ml-2">
                <Link to="/solutions/enterprise" onClick={closeMenu} className="block text-sm py-2 px-2 hover:text-[#8932c8] hover:bg-slate-50 rounded-md">
                  Enterprise
                </Link>
                <Link to="/solutions/smb" onClick={closeMenu} className="block text-sm py-2 px-2 hover:text-[#8932c8] hover:bg-slate-50 rounded-md">
                  SMB
                </Link>
                <Link to="/solutions/government" onClick={closeMenu} className="block text-sm py-2 px-2 hover:text-[#8932c8] hover:bg-slate-50 rounded-md">
                  Government
                </Link>
              </div>
            )}
          </div>
          
          {/* Partners Section */}
          <div className="border-b border-slate-100">
            <button 
              onClick={() => toggleSection('partners')}
              className="w-full flex items-center justify-between text-sm font-medium py-3 px-2 rounded-md hover:bg-slate-50"
            >
              <span>Partners</span>
              {expandedSection === 'partners' ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
            {expandedSection === 'partners' && (
              <div className="pl-4 pb-2 space-y-1 border-l-2 border-[#8932c8]/20 ml-2">
                <Link to="/partners/mssp" onClick={closeMenu} className="block text-sm py-2 px-2 hover:text-[#8932c8] hover:bg-slate-50 rounded-md">
                  MSSP Partners
                </Link>
                <Link to="/partners/reseller" onClick={closeMenu} className="block text-sm py-2 px-2 hover:text-[#8932c8] hover:bg-slate-50 rounded-md">
                  Reseller Partners
                </Link>
                <Link to="/partners/technology" onClick={closeMenu} className="block text-sm py-2 px-2 hover:text-[#8932c8] hover:bg-slate-50 rounded-md">
                  Technology Partners
                </Link>
              </div>
            )}
          </div>
          
          <Link 
            to="/blogs" 
            onClick={closeMenu}
            className="text-sm font-medium hover:text-[#8932c8] transition-colors py-3 px-2 rounded-md hover:bg-slate-50"
          >
            Insights
          </Link>
          <Link 
            to="/about" 
            onClick={closeMenu}
            className="text-sm font-medium hover:text-[#8932c8] transition-colors py-3 px-2 rounded-md hover:bg-slate-50"
          >
            About Us
          </Link>
          <Link 
            to="/contact" 
            onClick={closeMenu}
            className="mt-4"
          >
            <Button className="w-full bg-[#8932c8] hover:bg-[#7a2cb3]">
              Get Started
            </Button>
          </Link>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileMenu;